/**
 * BootLoader — 资源加载进度 + 加载完直入游戏（极简改造）
 * 追踪：DOM / 主题 CSS / 字体 / 游戏引擎 / 工作台组件 / Canvas 预热
 * 100% 后自动进入游戏（Game Mode）—— 无入口分流、无选择弹窗。
 */
import { useEffect, useRef, useState } from "react";
import { audioEngine } from "@/lib/audioEngine";

export interface BootLoaderProps {
  /** 加载完成后回调（直入游戏，无分流） */
  onReady: () => void;
}

interface Step {
  id: string;
  label: string;
  weight: number;
}

const STEPS: readonly Step[] = [
  { id: "dom", label: "DOM 骨架", weight: 10 },
  { id: "styles", label: "像素主题", weight: 15 },
  { id: "fonts", label: "像素字体", weight: 20 },
  { id: "engine", label: "游戏引擎", weight: 25 },
  { id: "vault", label: "钱柜组件", weight: 15 },
  { id: "canvas", label: "Canvas 预热", weight: 15 },
];

/** 加载完成到自动进入的展示间隔（ms） */
const AUTO_ENTER_MS = 320;

function clamp01(n: number): number {
  return Math.max(0, Math.min(100, n));
}

async function waitFonts(timeoutMs = 2500): Promise<void> {
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts?.ready) return;
  await Promise.race([
    fonts.ready.then(() => undefined),
    new Promise<void>((r) => window.setTimeout(r, timeoutMs)),
  ]);
}

function warmCanvas(): void {
  const c = document.createElement("canvas");
  c.width = 8;
  c.height = 8;
  const ctx = c.getContext("2d");
  if (ctx) {
    ctx.fillStyle = "#00f0ff";
    ctx.fillRect(0, 0, 8, 8);
    ctx.clearRect(0, 0, 8, 8);
  }
}

export function BootLoader({ onReady }: BootLoaderProps) {
  const [progress, setProgress] = useState(0);
  const [stepLabel, setStepLabel] = useState("BOOTING...");
  const [done, setDone] = useState(false);
  const startedRef = useRef(false);
  const enteredRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    const acc: Record<string, boolean> = {};

    const mark = async (id: string, fn: () => void | Promise<void>) => {
      const step = STEPS.find((s) => s.id === id);
      if (!step || cancelled) return;
      setStepLabel(step.label);
      try {
        await fn();
      } catch {
        /* single step failure does not block boot */
      }
      if (cancelled) return;
      acc[id] = true;
      const total = STEPS.reduce(
        (sum, s) => sum + (acc[s.id] ? s.weight : 0),
        0,
      );
      setProgress(clamp01(total));
    };

    const run = async () => {
      await mark("dom", () => {
        if (document.readyState === "loading") {
          return new Promise<void>((r) =>
            document.addEventListener("DOMContentLoaded", () => r(), {
              once: true,
            }),
          );
        }
      });
      await mark("styles", async () => {
        for (let i = 0; i < 20; i++) {
          const v = getComputedStyle(document.documentElement).getPropertyValue(
            "--px-cyan",
          );
          if (v && v.trim()) return;
          await new Promise((r) => requestAnimationFrame(() => r(null)));
        }
      });
      await mark("fonts", () => waitFonts());
      await mark("engine", async () => {
        await import("@/components/game/GameShell");
      });
      await mark("vault", async () => {
        await import("@/components/VaultInventory");
        await import("@/components/KernelHubTerminal");
      });
      await mark("canvas", async () => {
        warmCanvas();
        await new Promise((r) => requestAnimationFrame(() => r(null)));
      });

      if (!cancelled) {
        setProgress(100);
        setStepLabel("READY");
        setDone(true);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  /* 加载完成 → 短暂停留展示 READY → 自动直入游戏（无选择弹窗） */
  useEffect(() => {
    if (!done) return;
    const t = window.setTimeout(() => {
      if (enteredRef.current) return;
      enteredRef.current = true;
      void audioEngine.unlock();
      audioEngine.play("select");
      onReady();
    }, AUTO_ENTER_MS);
    return () => window.clearTimeout(t);
  }, [done, onReady]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-[#06080d] p-4"
      role="dialog"
      aria-label="启动加载"
      data-testid="boot-loader"
    >
      <div className="w-full max-w-md border border-[#00f0ff]/40 bg-[#0b1220]/95 p-6 shadow-[0_0_36px_rgba(0,240,255,0.25)]">
        <div className="text-center font-pixel text-[11px] tracking-[0.25em] text-[#00f0ff]">
          XIN<span className="text-[#ff7ad9]">.</span>XYZ
        </div>
        <div className="mt-1 text-center font-pixel text-[7px] tracking-[0.22em] text-[#8aa3c2]">
          NEURAL AGENT &amp; SELF-CUSTODY VAULT
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between font-pixel text-[8px] text-[#8aa3c2]">
            <span>{stepLabel}</span>
            <span className="tabular-nums text-[#00f0ff]">{progress}%</span>
          </div>
          <div className="mt-2 h-4 w-full border-2 border-[#00f0ff]/50 bg-[#06080d] p-[2px]">
            <div
              className="h-full transition-[width] duration-200"
              style={{
                width: `${progress}%`,
                background:
                  "repeating-linear-gradient(90deg,#00f0ff 0 8px,#38bdf8 8px 10px)",
                boxShadow: "0 0 12px rgba(0,240,255,0.55)",
              }}
            />
          </div>
          <div className="mt-2 grid grid-cols-3 gap-1">
            {STEPS.map((s) => (
              <div
                key={s.id}
                className={`font-pixel text-[6px] leading-4 ${
                  progress >= 100 ? "text-[#00f0ff]" : "text-[#4a6280]"
                }`}
              >
                {s.label}
              </div>
            ))}
          </div>
        </div>

        <p className="mt-5 text-center font-zpix text-[10px] text-[#8aa3c2]">
          {done ? "资源就绪 // 正在进入游戏..." : "加载中 // 请稍候"}
        </p>
      </div>
    </div>
  );
}

export default BootLoader;
